import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@nexus/ui";
import { useDeleteScheduledJob } from "@/lib/queries";
import { ScheduledJobMutationError } from "./ScheduledJobMutationError";

interface ScheduledJobDeleteDialogProps {
  jobId: string | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: (jobId: string) => void;
}

/**
 * Named confirm for destructive delete. Confirm stays disabled until the
 * operator types the job id (same string sent as `confirmJobId`).
 */
export function ScheduledJobDeleteDialog({
  jobId,
  onOpenChange,
  onDeleted,
}: ScheduledJobDeleteDialogProps) {
  const { t } = useTranslation();
  const remove = useDeleteScheduledJob();
  const [typedId, setTypedId] = useState("");

  const isOpen = !!jobId;

  useEffect(() => {
    if (!isOpen) return;
    setTypedId("");
  }, [isOpen, jobId]);

  const isAcknowledged = !!jobId && typedId === jobId;
  const canConfirm = isAcknowledged && !remove.isPending;

  function handleOpenChange(open: boolean) {
    if (remove.isPending) return;
    if (!open) remove.reset();
    onOpenChange(open);
  }

  function confirm() {
    if (!jobId || !canConfirm) return;
    remove.mutate(
      { jobId, confirmJobId: jobId },
      {
        onSuccess: () => {
          handleOpenChange(false);
          onDeleted?.(jobId);
        },
      },
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("admin.scheduledJobs.delete.title", "Delete scheduled job?")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.scheduledJobs.delete.body",
              "This removes the Cloud Scheduler job permanently. Type the job id to confirm.",
            )}
          </DialogDescription>
        </DialogHeader>

        {jobId ? (
          <div className="flex flex-col gap-3">
            <p
              className="rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-[11px] text-foreground"
              data-testid="scheduled-job-delete-id"
            >
              {jobId}
            </p>
            <div className="flex flex-col gap-2">
              <Label htmlFor="scheduled-job-delete-confirm">
                {t("admin.scheduledJobs.delete.confirmLabel", "Job id")}
              </Label>
              <Input
                id="scheduled-job-delete-confirm"
                value={typedId}
                disabled={remove.isPending}
                spellCheck={false}
                autoComplete="off"
                placeholder={jobId}
                onChange={(event) => setTypedId(event.target.value)}
                className="font-mono text-[11px]"
                aria-label={t(
                  "admin.scheduledJobs.delete.confirmAria",
                  "Type {{jobId}} to confirm delete",
                  { jobId },
                )}
              />
            </div>
            {remove.error ? (
              <ScheduledJobMutationError error={remove.error} onDismiss={() => remove.reset()} />
            ) : null}
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={remove.isPending}
            onClick={() => handleOpenChange(false)}
          >
            {t("admin.scheduledJobs.delete.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={!canConfirm}
            onClick={confirm}
          >
            {remove.isPending
              ? t("admin.scheduledJobs.delete.deleting", "Deleting…")
              : t("admin.scheduledJobs.delete.confirm", "Delete job")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
